'use client';

import {
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  CalendarClock,
} from 'lucide-react';
import {
  formatRelativeTime,
  statusLabel,
  statusToneClasses,
} from '../lib/format';
import type { SupportChannel } from '../services/support-api';
import type { AdminInboxTicket } from '../hooks/use-admin-support-inbox';

interface AdminInboxRowProps {
  ticket: AdminInboxTicket;
  onSelect: (id: string) => void;
}

const CHANNEL_ICONS: Record<SupportChannel, typeof MessageSquare> = {
  'in-app': MessageSquare,
  email: Mail,
  whatsapp: MessageCircle,
  phone: Phone,
  'scheduled-call': CalendarClock,
};

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function AdminInboxRow({ ticket, onSelect }: AdminInboxRowProps) {
  const Channel = CHANNEL_ICONS[ticket.channel] ?? MessageSquare;
  const lastMsg = ticket.messages?.[0];
  const needsReply = ticket.status === 'open' || ticket.status === 'pending_agent';

  return (
    <button
      type="button"
      onClick={() => onSelect(ticket.id)}
      className="group flex w-full items-start gap-2.5 rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-left transition-all hover:border-amber-300 hover:bg-amber-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-amber-700/50 dark:hover:bg-amber-950/20"
    >
      <div
        className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[0.625rem] font-semibold ${
          needsReply
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
            : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400'
        }`}
      >
        {initials(ticket.user?.name ?? null)}
        {needsReply && (
          <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-neutral-900" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[0.75rem] font-semibold text-neutral-900 dark:text-neutral-100">
            {ticket.user?.name ?? 'Client'}
          </span>
          <Channel
            className="h-2.5 w-2.5 shrink-0 text-neutral-300 dark:text-neutral-600"
            strokeWidth={2}
          />
          <span className="truncate text-[0.625rem] text-neutral-400 dark:text-neutral-600">
            · {ticket.tenant?.name ?? '—'}
          </span>
        </div>
        <div className="mt-0.5 truncate text-[0.6875rem] font-medium text-neutral-700 dark:text-neutral-300">
          {ticket.subject}
        </div>
        {lastMsg && (
          <div className="mt-0.5 truncate text-[0.6875rem] text-neutral-500 dark:text-neutral-500">
            {lastMsg.authorType === 'agent' ? '⤴︎ ' : ''}
            {lastMsg.body}
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        <span
          className={`rounded-full px-1.5 py-0.5 text-[0.5625rem] font-medium ${statusToneClasses(ticket.status)}`}
        >
          {statusLabel(ticket.status)}
        </span>
        <span className="text-[0.5625rem] uppercase tracking-wider text-neutral-400 dark:text-neutral-600">
          {formatRelativeTime(ticket.updatedAt)}
        </span>
      </div>
    </button>
  );
}
